import React from "react";

/**
 * BdrRowButton — a full-width row that GOES somewhere.
 *
 * Shares `BdrDisclosure`'s bar exactly (same classes, same `index.css` block),
 * with two deliberate differences:
 *
 *   - the chevron points RIGHT, not down. Down means "this opens in place";
 *     right means "this takes you elsewhere". When both kinds of row sit in one
 *     stack — as they do on the client page — that is the only thing telling
 *     you which is which before you click.
 *   - there is no body, so no open state and no animation.
 *
 * It exists because the client page stacks two of these among six disclosures,
 * and they were still wearing `.single-client-actions-btn` — a byte-for-byte
 * copy of the `header-buttons-collapse-sticky` recipe that BdrDisclosure
 * replaced. Same look, so one of them had to own it.
 *
 *   <BdrRowButton label="Messages" icon={<ForumIcon />} onClick={open} />
 */

const BdrRowButton = ({
  label,
  icon,
  onClick,
  /** anything to sit before the chevron — a count badge, a status chip */
  trailing,
  disabled = false,
  className = "",
  ariaLabel,
  ...rest
}) => {
  const rootClass = ["bdr-disclosure-header", "bdr-row-button", className]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={rootClass}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
      {...rest}
    >
      {/* `.bdr-disclosure-row` is pointer-events: none, which is harmless here
          — the button itself is the whole target and nothing inside it takes
          its own click. */}
      <span className="bdr-disclosure-row">
        {icon && <span className="bdr-disclosure-icon">{icon}</span>}

        <span className="bdr-disclosure-label">{label}</span>

        <span className="bdr-disclosure-gap" />

        {trailing}

        <svg
          className="bdr-disclosure-chevron"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M9 6l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
};

export default BdrRowButton;
